import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ujmsProbationDashboard, ujmsProbationCatalog, ujmsOpenTerm, ujmsLogCheckin,
  ujmsReportViolation, ujmsCompleteTerm, ujmsCases, ujmsCaseProbation,
  ujmsProbationReferrals, ujmsAcceptReferral, ujmsDeclineReferral, ujmsProbationOfficers,
  ujmsAssignOfficer, ujmsSetRisk, ujmsAddCondition, ujmsConditionProgress, ujmsPostLedger,
  ujmsSendReminder, ujmsProbationDocUrl, ujmsTermDetail,
} from "../../services/ujmsApi";
import { Panel, Badge, Stat, thCls, tdCls, inputCls, selectCls, Field, btnPrimary, btnGhost, fmtDT, fmtD, EmptyState } from "../ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { ClipboardList, AlertTriangle, Inbox, UserCheck, ShieldAlert, FileText, DollarSign, Bell, Settings2, CheckCircle2 } from "lucide-react";

export default function ProbationWorkspace() {
  const [data, setData] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [dialog, setDialog] = useState(null); // {type: 'term'|'checkin'|'violation', term}
  const [referrals, setReferrals] = useState([]);
  const [officers, setOfficers] = useState([]);
  const [accept, setAccept] = useState(null); // referral being accepted
  const [manage, setManage] = useState(null); // term being managed

  const load = useCallback(() => {
    ujmsProbationDashboard().then((r) => setData(r.data)).catch(() => setData(null));
    ujmsProbationReferrals("PENDING").then((r) => setReferrals(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); ujmsProbationCatalog().then((r) => setCatalog(r.data)).catch(() => {}); ujmsProbationOfficers().then((r) => setOfficers(r.data)).catch(() => {}); }, [load]);

  const complete = async (t) => {
    try { await ujmsCompleteTerm(t.id); toast.success("Supervision completed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!data) return <div className="text-sm text-slate-500 p-4" data-testid="probation-loading">Loading probation workspace…</div>;

  return (
    <div className="space-y-4" data-testid="probation-workspace">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Probation Services</h1>
          <p className="text-sm text-slate-500">Intake from sentencing, supervision, compliance, restitution and violations.</p>
        </div>
        <button className={btnPrimary} onClick={() => setDialog({ type: "term" })} data-testid="open-term-btn">
          <ClipboardList className="w-4 h-4" /> Open Supervision Term
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="New Referrals" value={referrals.length} testId="stat-referrals" tone={referrals.length ? "warn" : undefined} />
        <Stat label="Active Supervisions" value={data.active_count} testId="stat-active-terms" />
        <Stat label="Check-ins Due" value={data.checkins_due_count} testId="stat-checkins-due" tone={data.checkins_due_count ? "warn" : undefined} />
        <Stat label="Violations (30d)" value={data.violations_30d_count} testId="stat-violations" tone={data.violations_30d_count ? "warn" : undefined} />
        <Stat label="VOP Hearings Set" value={data.vop_hearings_count || 0} testId="stat-vop-hearings" tone={data.vop_hearings_count ? "warn" : undefined} />
      </div>

      {referrals.length > 0 && (
        <Panel title={<span className="inline-flex items-center gap-1.5"><Inbox className="w-4 h-4" /> Intake — new probation referrals from sentencing</span>} testId="probation-referrals">
          <table className="w-full border-collapse">
            <thead><tr><th className={thCls}>Case</th><th className={thCls}>Defendant</th><th className={thCls}>Supervision</th><th className={thCls}>Term</th><th className={thCls}>Restitution</th><th className={thCls}>Source order</th><th className={thCls}></th></tr></thead>
            <tbody>
              {referrals.map((r) => (
                <tr key={r.id} className="hover:bg-amber-50/60 bg-amber-50/30" data-testid={`referral-row-${r.id}`}>
                  <td className={`${tdCls} font-mono text-xs`}><Link className="text-blue-900 hover:underline" to={`/ujms/cases/${r.case_id}?tab=Probation`}>{r.case_number}</Link></td>
                  <td className={tdCls}>{r.defendant_name}</td>
                  <td className={`${tdCls} text-xs`}>{r.supervision_label}</td>
                  <td className={`${tdCls} text-xs`}>{r.duration_months} mo</td>
                  <td className={`${tdCls} text-xs`}>{r.restitution_ordered ? `$${Number(r.restitution_ordered).toLocaleString()}` : "—"}</td>
                  <td className={`${tdCls} text-xs text-slate-500`}>{r.order_title}</td>
                  <td className={`${tdCls} whitespace-nowrap`}>
                    <button className={btnPrimary} onClick={() => setAccept(r)} data-testid={`referral-accept-${r.id}`}><UserCheck className="w-3.5 h-3.5" /> Accept &amp; assign</button>
                    <button className={`${btnGhost} ml-1`} onClick={async () => { const reason = window.prompt("Reason for declining this referral?"); if (reason) { try { await ujmsDeclineReferral(r.id, { reason }); toast.success("Referral declined"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } } }} data-testid={`referral-decline-${r.id}`}>Decline</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      {(data.vop_hearings || []).length > 0 && (
        <Panel title="Upcoming violation-of-probation hearings" testId="probation-vop-hearings">
          <table className="w-full border-collapse">
            <thead><tr><th className={thCls}>Case</th><th className={thCls}>Defendant</th><th className={thCls}>Basis</th><th className={thCls}>PO recommendation</th><th className={thCls}>Hearing</th><th className={thCls}>Judge</th></tr></thead>
            <tbody>
              {data.vop_hearings.map((h) => (
                <tr key={h.violation_id} className="hover:bg-slate-50" data-testid={`vop-hearing-${h.violation_id}`}>
                  <td className={`${tdCls} font-mono text-xs`}><Link className="text-blue-900 hover:underline" to={`/ujms/cases/${h.case_id}?tab=Probation`}>{h.case_number}</Link></td>
                  <td className={tdCls}>{h.defendant_name}</td>
                  <td className={`${tdCls} text-xs`}>{(h.category || "").replace(/_/g, " ")} <Badge value={h.severity} /></td>
                  <td className={`${tdCls} text-xs italic text-slate-500`}>{h.recommendation || "—"}</td>
                  <td className={`${tdCls} font-mono text-xs whitespace-nowrap`}>{h.scheduled_start ? fmtDT(h.scheduled_start) : "TBD"}<div className="text-[10px] text-slate-400">{h.courtroom}</div></td>
                  <td className={`${tdCls} text-xs`}>{h.judicial_officer || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      <Panel title="Supervision caseload" testId="probation-caseload">
        <table className="w-full border-collapse">
          <thead><tr>
            <th className={thCls}>Case</th><th className={thCls}>Defendant</th><th className={thCls}>Type</th>
            <th className={thCls}>Officer</th><th className={thCls}>Risk</th>
            <th className={thCls}>Term</th><th className={thCls}>Conditions</th><th className={thCls}>Last Contact</th>
            <th className={thCls}>Violations</th><th className={thCls}>Status</th><th className={thCls}></th>
          </tr></thead>
          <tbody>
            {data.caseload.map((t) => (
              <tr key={t.id} className="hover:bg-slate-50" data-testid={`term-row-${t.id}`}>
                <td className={`${tdCls} font-mono text-xs`}><Link className="text-blue-900 hover:underline" to={`/ujms/cases/${t.case_id}?tab=Probation`}>{t.case_number}</Link></td>
                <td className={tdCls}>{t.defendant_name}</td>
                <td className={`${tdCls} text-xs`}>{t.supervision_label}</td>
                <td className={`${tdCls} text-xs`}>{t.officer_name || <span className="text-amber-700">Unassigned</span>}</td>
                <td className={`${tdCls} text-xs`}>{t.risk?.level ? <Badge value={t.risk.level} /> : <span className="text-slate-400">—</span>}</td>
                <td className={`${tdCls} font-mono text-xs`}>{t.start_date} → {t.end_date}</td>
                <td className={`${tdCls} text-xs`}>{(() => { const items = t.condition_items || []; const done = items.filter((c) => c.status === "COMPLETED").length; return items.length ? `${done}/${items.length} met` : (t.conditions || []).length; })()}</td>
                <td className={`${tdCls} text-xs`}>
                  {t.last_checkin ? `${fmtD(t.last_checkin.occurred_at)} · ${t.last_checkin.result}` : "None"}
                  {t.checkin_due && <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-semibold text-orange-700 bg-orange-50 border border-orange-200 rounded-sm px-1"><AlertTriangle className="w-2.5 h-2.5" /> DUE</span>}
                </td>
                <td className={`${tdCls} text-xs ${t.open_violations ? "text-red-700 font-semibold" : ""}`}>{t.open_violations || 0} open</td>
                <td className={tdCls}><Badge value={t.status} /></td>
                <td className={`${tdCls} whitespace-nowrap`}>
                  <button className={btnPrimary} onClick={() => setManage(t)} data-testid={`manage-btn-${t.id}`}><Settings2 className="w-3.5 h-3.5" /> Manage</button>
                  <button className={`${btnGhost} ml-1`} onClick={() => setDialog({ type: "checkin", term: t })} data-testid={`checkin-btn-${t.id}`}>Check-in</button>
                  <button className={`${btnGhost} ml-1 border-red-300 text-red-700`} onClick={() => setDialog({ type: "violation", term: t })} data-testid={`violation-btn-${t.id}`}>Violation</button>
                </td>
              </tr>
            ))}
            {!data.caseload.length && <tr><td colSpan={11} className={tdCls}>No active supervision terms.</td></tr>}
          </tbody>
        </table>
      </Panel>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Panel title="Recent violations (30 days)" testId="probation-violations">
          {data.recent_violations.length === 0 ? (
            <EmptyState icon={AlertTriangle} title="No violations reported" note="Violations you report route directly to the assigned judge for a VOP determination." testId="violations-empty" />
          ) : (
            <table className="w-full border-collapse"><tbody>
              {data.recent_violations.map((v) => (
                <tr key={v.id} data-testid={`violation-row-${v.id}`}>
                  <td className={`${tdCls} font-mono text-xs whitespace-nowrap`}><Link className="text-blue-900 hover:underline" to={`/ujms/cases/${v.case_id}?tab=Probation`}>{v.case_number}</Link></td>
                  <td className={tdCls}><span className="text-xs font-semibold">{v.category.replace(/_/g, " ")}</span> — {v.defendant_name}
                    <div className="text-xs text-slate-500 line-clamp-1">{v.description}</div></td>
                  <td className={tdCls}><Badge value={v.severity} /></td>
                  <td className={tdCls}><Badge value={v.status} />{v.resolution && <div className="text-[10px] font-mono text-slate-500 mt-0.5">{v.resolution}</div>}</td>
                </tr>
              ))}
            </tbody></table>
          )}
        </Panel>
        <Panel title="My department tasks" testId="probation-tasks">
          <table className="w-full border-collapse"><tbody>
            {data.tasks.map((t) => (
              <tr key={t.id}><td className={tdCls}>{t.title}<div className="text-xs font-mono text-slate-400">{t.type}</div></td>
                <td className={tdCls}><Badge value={t.priority} /></td>
                <td className={`${tdCls} font-mono text-xs`}>{t.due_date}</td></tr>
            ))}
            {!data.tasks.length && <tr><td className={tdCls}>No open tasks for Probation.</td></tr>}
          </tbody></table>
        </Panel>
      </div>

      <OpenTermDialog open={dialog?.type === "term"} catalog={catalog} officers={officers} onClose={() => setDialog(null)} onDone={() => { setDialog(null); load(); }} />
      <CheckinDialog open={dialog?.type === "checkin"} term={dialog?.term} catalog={catalog} onClose={() => setDialog(null)} onDone={() => { setDialog(null); load(); }} />
      <ViolationDialog open={dialog?.type === "violation"} term={dialog?.term} catalog={catalog} onClose={() => setDialog(null)} onDone={() => { setDialog(null); load(); }} />
      <AcceptReferralDialog referral={accept} catalog={catalog} officers={officers} onClose={() => setAccept(null)} onDone={() => { setAccept(null); load(); }} />
      <TermManageDialog termRow={manage} catalog={catalog} officers={officers} onClose={() => setManage(null)} onChanged={load} />
    </div>
  );
}

function OpenTermDialog({ open, catalog, officers, onClose, onDone }) {
  const [caseNo, setCaseNo] = useState("");
  const [found, setFound] = useState(null);
  const [orders, setOrders] = useState([]);
  const [f, setF] = useState({ order_id: "", supervision_type: "PROBATION", duration_months: 12, conditions: [], notes: "", officer_user_id: "" });
  const [busy, setBusy] = useState(false);

  const lookup = async () => {
    try {
      const r = await ujmsCases({ case_number: caseNo.trim() });
      const c = (Array.isArray(r.data) ? r.data : r.data.items || [])[0];
      if (!c) { toast.error("Case not found"); setFound(null); return; }
      setFound(c);
      const p = await ujmsCaseProbation(c.id);
      setOrders(p.data.eligible_orders || []);
      if (!(p.data.eligible_orders || []).length) toast.warning("No signed/entered orders on this case — a term must be tied to one");
    } catch (e) { toast.error(e.response?.data?.detail || "Lookup failed"); }
  };

  const toggleCond = (c) => setF((s) => ({ ...s, conditions: s.conditions.includes(c) ? s.conditions.filter((x) => x !== c) : [...s.conditions, c] }));

  const submit = async () => {
    if (!found || !f.order_id) { toast.error("Select a case and sentencing order"); return; }
    setBusy(true);
    try {
      await ujmsOpenTerm({ case_id: found.id, ...f, duration_months: Number(f.duration_months) });
      toast.success("Supervision term opened");
      setCaseNo(""); setFound(null); setOrders([]); setF({ order_id: "", supervision_type: "PROBATION", duration_months: 12, conditions: [], notes: "", officer_user_id: "" });
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to open term"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="term-dialog">
        <DialogHeader><DialogTitle>Open Supervision Term</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Case number" required>
            <div className="flex gap-2">
              <input className={inputCls} value={caseNo} onChange={(e) => setCaseNo(e.target.value)} placeholder="e.g. 2026-CR-700001" data-testid="term-case-input" />
              <button className={btnGhost} onClick={lookup} data-testid="term-load-btn">Load</button>
            </div>
            {found && <div className="text-xs text-emerald-700 mt-1" data-testid="term-case-found">{found.case_number} — {found.caption}</div>}
          </Field>
          <Field label="Sentencing order (signed/entered)" required>
            <select className={selectCls} value={f.order_id} onChange={(e) => setF({ ...f, order_id: e.target.value })} data-testid="term-order-select">
              <option value="">Select order…</option>
              {orders.map((o) => <option key={o.id} value={o.id}>{o.title} [{o.status}]</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Supervision type">
              <select className={selectCls} value={f.supervision_type} onChange={(e) => setF({ ...f, supervision_type: e.target.value })} data-testid="term-type-select">
                {Object.entries(catalog?.supervision_types || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <Field label="Duration (months)">
              <input type="number" min="1" max="120" className={inputCls} value={f.duration_months} onChange={(e) => setF({ ...f, duration_months: e.target.value })} data-testid="term-duration-input" />
            </Field>
          </div>
          <Field label="Officer of record">
            <select className={selectCls} value={f.officer_user_id} onChange={(e) => setF({ ...f, officer_user_id: e.target.value })} data-testid="term-officer-select">
              <option value="">Me (current officer)</option>
              {(officers || []).map((o) => <option key={o.id} value={o.id}>{o.name} ({o.active_caseload} active)</option>)}
            </select>
          </Field>
          <Field label="Conditions of supervision">
            <div className="max-h-36 overflow-y-auto border border-slate-200 rounded-sm p-2 space-y-1">
              {(catalog?.conditions || []).map((c, i) => (
                <label key={c} className="flex items-center gap-2 text-xs text-slate-700">
                  <input type="checkbox" checked={f.conditions.includes(c)} onChange={() => toggleCond(c)} data-testid={`term-cond-${i}`} /> {c}
                </label>
              ))}
            </div>
          </Field>
          <Field label="Notes">
            <textarea className={inputCls} rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="term-notes" />
          </Field>
        </div>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} onClick={submit} disabled={busy} data-testid="term-submit-btn">Open Term</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CheckinDialog({ open, term, catalog, onClose, onDone }) {
  const [f, setF] = useState({ checkin_type: "OFFICE_VISIT", result: "COMPLIANT", note: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await ujmsLogCheckin(term.id, f); toast.success("Check-in logged"); setF({ checkin_type: "OFFICE_VISIT", result: "COMPLIANT", note: "" }); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="checkin-dialog">
        <DialogHeader><DialogTitle>Log Check-in — {term?.defendant_name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Type">
            <select className={selectCls} value={f.checkin_type} onChange={(e) => setF({ ...f, checkin_type: e.target.value })} data-testid="checkin-type-select">
              {(catalog?.checkin_types || []).map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
            </select>
          </Field>
          <Field label="Result">
            <select className={selectCls} value={f.result} onChange={(e) => setF({ ...f, result: e.target.value })} data-testid="checkin-result-select">
              {(catalog?.checkin_results || []).map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Note">
          <textarea className={inputCls} rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="checkin-note" />
        </Field>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} onClick={submit} disabled={busy} data-testid="checkin-submit-btn">Log Check-in</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ViolationDialog({ open, term, catalog, onClose, onDone }) {
  const [f, setF] = useState({ category: "MISSED_CHECKIN", severity: "MAJOR", description: "", recommendation: "", hearing_date: "", request_warrant: false, warrant_type: "BENCH", warrant_bond: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const reset = () => setF({ category: "MISSED_CHECKIN", severity: "MAJOR", description: "", recommendation: "", hearing_date: "", request_warrant: false, warrant_type: "BENCH", warrant_bond: "" });
  const submit = async () => {
    if (!f.description.trim()) { toast.error("Description is required"); return; }
    setBusy(true);
    try {
      const body = { category: f.category, severity: f.severity, description: f.description, recommendation: f.recommendation, hearing_date: f.hearing_date || null, request_warrant: f.request_warrant, warrant_type: f.warrant_type, warrant_bond: f.warrant_bond ? Number(f.warrant_bond) : null };
      const r = await ujmsReportViolation(term.id, body);
      toast.success("Petition filed — VOP hearing set" + (r.data?.hearing ? ` for ${r.data.hearing.date} ${r.data.hearing.start_time}` : ""));
      setResult(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const close = () => { reset(); setResult(null); onClose(); };
  const done = () => { reset(); setResult(null); onDone(); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="violation-dialog">
        <DialogHeader><DialogTitle>Petition for Violation of Probation — {term?.defendant_name}</DialogTitle></DialogHeader>
        {result ? (
          <div className="space-y-2 text-sm" data-testid="violation-result">
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2">Petition filed. {result.hearing ? <>VOP hearing set for <b>{result.hearing.date} at {result.hearing.start_time}</b> before {result.hearing.judicial_officer || "the assigned judge"} ({result.hearing.courtroom || "courtroom TBD"}).</> : "A judge will rule on the petition."}</div>
            {result.warrant && <div className="bg-red-50 border border-red-200 rounded-sm px-3 py-2" data-testid="violation-result-warrant">{result.warrant.status === "ISSUED" ? "Bench warrant ISSUED" : "Bench warrant REQUESTED (awaiting judicial issuance)"} — {result.warrant.warrant_type}{result.warrant.bond_amount ? ` · bond $${Number(result.warrant.bond_amount).toLocaleString()}` : ""}.</div>}
            <DialogFooter><button className={btnPrimary} onClick={done} data-testid="violation-result-done">Done</button></DialogFooter>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Category">
                <select className={selectCls} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="violation-category-select">
                  {(catalog?.violation_categories || []).map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                </select>
              </Field>
              <Field label="Severity">
                <select className={selectCls} value={f.severity} onChange={(e) => setF({ ...f, severity: e.target.value })} data-testid="violation-severity-select">
                  {(catalog?.severities || []).map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Description" required>
              <textarea className={inputCls} rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="violation-desc" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="PO recommendation to the court">
                <input className={inputCls} value={f.recommendation} placeholder="e.g. Revoke and re-sentence" onChange={(e) => setF({ ...f, recommendation: e.target.value })} data-testid="violation-recommendation" />
              </Field>
              <Field label="Requested hearing date (optional)">
                <input type="date" className={inputCls} value={f.hearing_date} onChange={(e) => setF({ ...f, hearing_date: e.target.value })} data-testid="violation-hearing-date" />
              </Field>
            </div>
            <div className="border border-red-200 rounded-sm p-2 bg-red-50/40">
              <label className="flex items-center gap-2 text-sm font-medium text-red-800"><input type="checkbox" checked={f.request_warrant} onChange={(e) => setF({ ...f, request_warrant: e.target.checked })} data-testid="violation-request-warrant" /> Request a warrant before the hearing (absconded / new arrest)</label>
              {f.request_warrant && (
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <Field label="Warrant type">
                    <select className={selectCls} value={f.warrant_type} onChange={(e) => setF({ ...f, warrant_type: e.target.value })} data-testid="violation-warrant-type">
                      <option value="BENCH">BENCH</option><option value="ARREST">ARREST</option>
                    </select>
                  </Field>
                  <Field label="Suggested bond ($)">
                    <input type="number" className={inputCls} value={f.warrant_bond} onChange={(e) => setF({ ...f, warrant_bond: e.target.value })} data-testid="violation-warrant-bond" />
                  </Field>
                </div>
              )}
            </div>
            <div className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-sm px-2 py-1.5">
              Filing posts a certified docket entry, auto-schedules a Violation-of-Probation hearing on the court call, and notifies the assigned judge.
            </div>
            <DialogFooter>
              <button className={btnGhost} onClick={close}>Cancel</button>
              <button className={btnPrimary} onClick={submit} disabled={busy} data-testid="violation-submit-btn">File VOP Petition</button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}


function AcceptReferralDialog({ referral, catalog, officers, onClose, onDone }) {
  const [f, setF] = useState({ officer_user_id: "", supervision_type: "", duration_months: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (referral) setF({ officer_user_id: "", supervision_type: referral.supervision_type, duration_months: referral.duration_months }); }, [referral]);
  if (!referral) return null;
  const submit = async () => {
    setBusy(true);
    try {
      await ujmsAcceptReferral(referral.id, {
        officer_user_id: f.officer_user_id || null,
        supervision_type: f.supervision_type, duration_months: Number(f.duration_months),
      });
      toast.success("Referral accepted — supervision term opened");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to accept"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!referral} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="accept-referral-dialog">
        <DialogHeader><DialogTitle>Accept referral — {referral.defendant_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-sm px-2 py-1.5">
            {referral.case_number} · from <b>{referral.order_title}</b>. Conditions, length{referral.restitution_ordered ? `, $${Number(referral.restitution_ordered).toLocaleString()} restitution` : ""} are pulled from the sentencing order.
          </div>
          <Field label="Assign to probation officer" required>
            <select className={selectCls} value={f.officer_user_id} onChange={(e) => setF({ ...f, officer_user_id: e.target.value })} data-testid="accept-officer-select">
              <option value="">Me (current officer)</option>
              {(officers || []).map((o) => <option key={o.id} value={o.id}>{o.name} ({o.active_caseload} active)</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Supervision type">
              <select className={selectCls} value={f.supervision_type} onChange={(e) => setF({ ...f, supervision_type: e.target.value })} data-testid="accept-type-select">
                {Object.entries(catalog?.supervision_types || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <Field label="Duration (months)">
              <input type="number" min="1" max="120" className={inputCls} value={f.duration_months} onChange={(e) => setF({ ...f, duration_months: e.target.value })} data-testid="accept-duration-input" />
            </Field>
          </div>
        </div>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} onClick={submit} disabled={busy} data-testid="accept-submit-btn">Open Supervision</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TermManageDialog({ termRow, catalog, officers, onClose, onChanged }) {
  const [t, setT] = useState(null);
  const [tab, setTab] = useState("conditions");
  const reload = useCallback(() => { if (termRow) ujmsTermDetail(termRow.id).then((r) => setT(r.data)).catch(() => {}); }, [termRow]);
  useEffect(() => { reload(); setTab("conditions"); }, [reload]);
  if (!termRow) return null;
  const refresh = () => { reload(); onChanged?.(); };
  const fin = t?.financials || {};
  const tabs = [["conditions", "Conditions", ShieldAlert], ["financials", "Restitution & fees", DollarSign], ["risk", "Risk", AlertTriangle], ["officer", "Officer", UserCheck], ["documents", "Documents", FileText], ["reminders", "Reminders", Bell]];
  if (t && t.status === "ACTIVE") tabs.push(["discharge", "Discharge", CheckCircle2]);
  return (
    <Dialog open={!!termRow} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="term-manage-dialog">
        <DialogHeader><DialogTitle>Supervision — {termRow.defendant_name} <span className="font-mono text-xs text-slate-400">{termRow.case_number}</span></DialogTitle></DialogHeader>
        {!t ? <div className="text-sm text-slate-500 py-6">Loading…</div> : (
          <>
            <div className="flex gap-1 border-b border-slate-200 mb-3 flex-wrap">
              {tabs.map(([k, label, Icon]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`term-tab-${k}`}
                  className={`px-2.5 py-1.5 text-xs font-medium inline-flex items-center gap-1 border-b-2 -mb-px ${tab === k ? "border-blue-800 text-blue-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
                  <Icon className="w-3.5 h-3.5" /> {label}
                </button>
              ))}
            </div>
            <div className="max-h-[52vh] overflow-y-auto pr-1">
              {tab === "conditions" && <ConditionsTab term={t} onChanged={refresh} />}
              {tab === "financials" && <FinancialsTab term={t} fin={fin} onChanged={refresh} />}
              {tab === "risk" && <RiskTab term={t} catalog={catalog} onChanged={refresh} />}
              {tab === "officer" && <OfficerTab term={t} officers={officers} onChanged={refresh} />}
              {tab === "documents" && <DocumentsTab term={t} />}
              {tab === "reminders" && <RemindersTab term={t} onChanged={refresh} />}
              {tab === "discharge" && <DischargeTab term={t} onDone={() => { refresh(); onClose(); }} />}
            </div>
          </>
        )}
        <DialogFooter><button className={btnGhost} onClick={onClose}>Close</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConditionsTab({ term, onChanged }) {
  const [newCond, setNewCond] = useState("");
  const items = term.condition_items || [];
  const update = async (ci, body) => { try { await ujmsConditionProgress(term.id, ci.id, body); toast.success("Updated"); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const add = async () => { if (!newCond.trim()) return; try { await ujmsAddCondition(term.id, { label: newCond.trim() }); setNewCond(""); toast.success("Condition added"); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  return (
    <div className="space-y-2" data-testid="conditions-tab">
      {!items.length && <p className="text-xs italic text-slate-400">No structured conditions recorded.</p>}
      {items.map((ci) => (
        <div key={ci.id} className="border border-slate-200 rounded-sm px-2 py-1.5 flex items-center gap-2" data-testid={`cond-${ci.id}`}>
          <div className="flex-1">
            <div className="text-xs font-medium text-slate-800">{ci.label}</div>
            <div className="text-[10px] text-slate-400">{ci.type}{ci.target ? ` · ${ci.completed || 0}/${ci.target}` : ""}{ci.note ? ` · ${ci.note}` : ""}</div>
          </div>
          {ci.type === "COMMUNITY_SERVICE" && <button className={btnGhost} onClick={() => { const h = window.prompt("Add hours completed:"); if (h) update(ci, { completed_delta: Number(h) }); }} data-testid={`cond-hours-${ci.id}`}>+ hrs</button>}
          <select className={cls2(selectCls, "w-36")} value={ci.status} onChange={(e) => update(ci, { status: e.target.value })} data-testid={`cond-status-${ci.id}`}>
            {(["PENDING", "IN_PROGRESS", "COMPLETED", "NONCOMPLIANT", "WAIVED"]).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      ))}
      <div className="flex gap-2 pt-1">
        <input className={inputCls} placeholder="Add a condition…" value={newCond} onChange={(e) => setNewCond(e.target.value)} data-testid="cond-add-input" />
        <button className={btnGhost} onClick={add} data-testid="cond-add-btn">Add</button>
      </div>
    </div>
  );
}

function FinancialsTab({ term, fin, onChanged }) {
  const [f, setF] = useState({ kind: "RESTITUTION_PAYMENT", amount: "", note: "" });
  const post = async () => { if (!Number(f.amount)) { toast.error("Enter an amount"); return; } try { await ujmsPostLedger(term.id, { ...f, amount: Number(f.amount) }); setF({ kind: f.kind, amount: "", note: "" }); toast.success("Posted"); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const rBal = (fin.restitution_ordered || 0) - (fin.restitution_paid || 0);
  const fBal = (fin.fees_ordered || 0) - (fin.fees_paid || 0);
  return (
    <div className="space-y-3" data-testid="financials-tab">
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="border border-slate-200 rounded-sm px-2 py-1.5"><div className="text-slate-500">Restitution</div><div>Ordered {money(fin.restitution_ordered)} · Paid {money(fin.restitution_paid)}</div><div className={rBal > 0 ? "text-red-700 font-semibold" : "text-emerald-700 font-semibold"}>Balance {money(rBal)}</div></div>
        <div className="border border-slate-200 rounded-sm px-2 py-1.5"><div className="text-slate-500">Fees / fines</div><div>Ordered {money(fin.fees_ordered)} · Paid {money(fin.fees_paid)}</div><div className={fBal > 0 ? "text-red-700 font-semibold" : "text-emerald-700 font-semibold"}>Balance {money(fBal)}</div></div>
      </div>
      <div className="grid grid-cols-3 gap-2 items-end">
        <Field label="Entry"><select className={selectCls} value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} data-testid="ledger-kind">
          <option value="RESTITUTION_PAYMENT">Restitution payment</option><option value="FEE_PAYMENT">Fee payment</option>
          <option value="RESTITUTION_ASSESSMENT">Add restitution</option><option value="FEE_ASSESSMENT">Add fee</option>
        </select></Field>
        <Field label="Amount ($)"><input type="number" className={inputCls} value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="ledger-amount" /></Field>
        <button className={btnPrimary} onClick={post} data-testid="ledger-post-btn">Post</button>
      </div>
      {(fin.ledger || []).length > 0 && <table className="w-full text-[11px] border-collapse"><tbody>
        {fin.ledger.slice().reverse().map((l) => <tr key={l.id} className="border-b border-slate-100"><td className="py-0.5 font-mono">{l.date}</td><td>{l.kind.replace(/_/g, " ")}</td><td className="text-right font-mono">{money(l.amount)}</td><td className="text-slate-400">{l.by}</td></tr>)}
      </tbody></table>}
    </div>
  );
}

function RiskTab({ term, catalog, onChanged }) {
  const [f, setF] = useState({ level: term.risk?.level || "MODERATE", score: term.risk?.score || "", notes: term.risk?.notes || "" });
  const save = async () => { try { await ujmsSetRisk(term.id, { level: f.level, score: f.score ? Number(f.score) : null, notes: f.notes }); toast.success("Risk assessment saved"); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  return (
    <div className="space-y-3" data-testid="risk-tab">
      {term.risk?.level && <div className="text-xs text-slate-500">Last assessed by {term.risk.assessed_by} · {fmtD(term.risk.assessed_at)}</div>}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Risk level"><select className={selectCls} value={f.level} onChange={(e) => setF({ ...f, level: e.target.value })} data-testid="risk-level">{(catalog?.risk_levels || []).map((r) => <option key={r} value={r}>{r}</option>)}</select></Field>
        <Field label="Score (optional)"><input type="number" className={inputCls} value={f.score} onChange={(e) => setF({ ...f, score: e.target.value })} data-testid="risk-score" /></Field>
      </div>
      <Field label="Notes"><textarea className={inputCls} rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="risk-notes" /></Field>
      <button className={btnPrimary} onClick={save} data-testid="risk-save-btn">Save assessment</button>
    </div>
  );
}

function OfficerTab({ term, officers, onChanged }) {
  const [sel, setSel] = useState("");
  const assign = async () => { if (!sel) return; try { await ujmsAssignOfficer(term.id, { officer_user_id: sel }); toast.success("Officer assigned"); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  return (
    <div className="space-y-3" data-testid="officer-tab">
      <div className="text-sm">Current officer of record: <b>{term.officer_name || "Unassigned"}</b></div>
      <Field label="Reassign to"><select className={selectCls} value={sel} onChange={(e) => setSel(e.target.value)} data-testid="assign-officer-select"><option value="">Select officer…</option>{(officers || []).map((o) => <option key={o.id} value={o.id}>{o.name} ({o.active_caseload} active)</option>)}</select></Field>
      <button className={btnPrimary} onClick={assign} disabled={!sel} data-testid="assign-officer-btn">Assign</button>
    </div>
  );
}

function DocumentsTab({ term }) {
  const docs = [["ORDER_OF_PROBATION", "Order of Probation"], ["CONDITIONS_SHEET", "Conditions of Supervision"], ["DISCHARGE_CERTIFICATE", "Certificate of Discharge"]];
  return (
    <div className="space-y-2" data-testid="documents-tab">
      <p className="text-xs text-slate-500">Open a printable probation document (PDF) for this supervision term.</p>
      {docs.map(([k, label]) => (
        <a key={k} href={ujmsProbationDocUrl(term.id, k)} target="_blank" rel="noopener noreferrer"
          className={`${btnGhost} w-full justify-start`} data-testid={`doc-${k}`}><FileText className="w-4 h-4" /> {label}</a>
      ))}
    </div>
  );
}

function RemindersTab({ term, onChanged }) {
  const [f, setF] = useState({ message: "", channel: "SMS", remind_at: "" });
  const send = async () => { if (!f.message.trim()) { toast.error("Enter a message"); return; } try { await ujmsSendReminder(term.id, f); setF({ message: "", channel: "SMS", remind_at: "" }); toast.success("Reminder logged to the probationer"); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  return (
    <div className="space-y-3" data-testid="reminders-tab">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Channel"><select className={selectCls} value={f.channel} onChange={(e) => setF({ ...f, channel: e.target.value })} data-testid="remind-channel"><option>SMS</option><option>EMAIL</option><option>CALL</option></select></Field>
        <Field label="Date (optional)"><input type="date" className={inputCls} value={f.remind_at} onChange={(e) => setF({ ...f, remind_at: e.target.value })} data-testid="remind-date" /></Field>
      </div>
      <Field label="Message to probationer"><textarea className={inputCls} rows={2} value={f.message} placeholder="Reminder: your next court date / office visit is…" onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="remind-message" /></Field>
      <button className={btnPrimary} onClick={send} data-testid="remind-send-btn">Send reminder</button>
      <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1">Delivery is SIMULATED in this environment (logged to the messaging ledger).</div>
      {(term.reminders || []).length > 0 && <table className="w-full text-[11px] border-collapse"><tbody>
        {term.reminders.slice().reverse().map((r) => <tr key={r.id} className="border-b border-slate-100"><td className="py-0.5">{r.channel}</td><td>{r.message}</td><td className="text-slate-400 font-mono">{fmtD(r.sent_at)}</td></tr>)}
      </tbody></table>}
    </div>
  );
}

function DischargeTab({ term, onDone }) {
  const [f, setF] = useState({ discharge_type: "SATISFACTORY", reason: "" });
  const [busy, setBusy] = useState(false);
  const fin = term.financials || {};
  const rBal = (fin.restitution_ordered || 0) - (fin.restitution_paid || 0);
  const openConds = (term.condition_items || []).filter((c) => !["COMPLETED", "WAIVED"].includes(c.status)).length;
  const submit = async () => {
    setBusy(true);
    try {
      await ujmsCompleteTerm(term.id, f);
      toast.success("Supervision discharged — certificate ready");
      window.open(ujmsProbationDocUrl(term.id, "DISCHARGE_CERTIFICATE"), "_blank", "noopener");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to discharge"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="discharge-tab">
      <p className="text-xs text-slate-600">Close this supervision term and generate the Certificate of Discharge. A judge must rule on any unresolved violation first.</p>
      {(rBal > 0 || openConds > 0) && (
        <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5" data-testid="discharge-warning">
          ⚠ Outstanding: {openConds > 0 ? `${openConds} condition(s) not met` : ""}{openConds > 0 && rBal > 0 ? "; " : ""}{rBal > 0 ? `$${rBal.toLocaleString(undefined, { minimumFractionDigits: 2 })} restitution balance` : ""}. Consider an Unsatisfactory discharge.
        </div>
      )}
      <Field label="Discharge type">
        <select className={selectCls} value={f.discharge_type} onChange={(e) => setF({ ...f, discharge_type: e.target.value })} data-testid="discharge-type">
          <option value="SATISFACTORY">Satisfactory completion</option>
          <option value="EARLY_TERMINATION">Early termination (by the Court)</option>
          <option value="UNSATISFACTORY">Unsatisfactory discharge</option>
        </select>
      </Field>
      <Field label="Reason / note (optional)"><textarea className={inputCls} rows={2} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="discharge-reason" /></Field>
      <button className={btnPrimary} onClick={submit} disabled={busy} data-testid="discharge-submit-btn"><CheckCircle2 className="w-4 h-4" /> Discharge &amp; generate certificate</button>
    </div>
  );
}


function cls2(...a) { return a.filter(Boolean).join(" "); }
